import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const StaffDaysOffModal = ({ staff, providerId, sessionToken, onClose }) => {
  const [daysOff, setDaysOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [affectedAppointments, setAffectedAppointments] = useState([]);
  const [showAffected, setShowAffected] = useState(false);
  
  const [form, setForm] = useState({
    date: '',
    is_full_day: true,
    start_time: '09:00',
    end_time: '17:00',
    reason: ''
  });

  const fetchDaysOff = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/provider/staff/${staff.id}/days-off`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setDaysOff(res.data.days_off || []);
    } catch (e) {
      console.error('Failed to fetch days off:', e);
    } finally {
      setLoading(false);
    }
  }, [staff.id, sessionToken]);

  useEffect(() => { fetchDaysOff(); }, [fetchDaysOff]);

  const handleAdd = async () => {
    if (!form.date) { alert('Please select a date'); return; }
    if (!form.is_full_day && form.start_time >= form.end_time) {
      alert('End time must be after start time'); return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/provider/staff/${staff.id}/days-off`,
        form,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (res.data.affected_count > 0) {
        setAffectedAppointments(res.data.affected_appointments);
        setShowAffected(true);
      }
      
      setShowAddForm(false);
      setForm({ date: '', is_full_day: true, start_time: '09:00', end_time: '17:00', reason: '' });
      fetchDaysOff();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add day off');
    }
    setSaving(false);
  };

  const handleRemove = async (dayOffId) => {
    if (!window.confirm('Remove this day off?')) return;
    try {
      await axios.delete(`${API}/api/provider/staff/days-off/${dayOffId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      fetchDaysOff();
    } catch (e) {
      alert('Failed to remove day off');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Separate upcoming and past
  const upcoming = daysOff.filter(d => d.date >= today);
  const past = daysOff.filter(d => d.date < today);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Days Off — {staff.name}</h3>
            <p className="text-sm text-gray-500">Manage time off and partial days</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-days-off-modal">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Add New Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full mb-4 py-2.5 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-colors text-sm font-medium"
              data-testid="add-day-off-btn"
            >
              + Add Day Off
            </button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    min={today}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="day-off-date-input"
                  />
                </div>

                {/* Full Day / Partial Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setForm({ ...form, is_full_day: true })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        form.is_full_day
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid="full-day-off-btn"
                    >
                      Full Day Off
                    </button>
                    <button
                      onClick={() => setForm({ ...form, is_full_day: false })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        !form.is_full_day
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid="partial-day-off-btn"
                    >
                      Partial Day
                    </button>
                  </div>
                </div>

                {/* Time Range (for partial) */}
                {!form.is_full_day && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                      <input
                        type="time"
                        value={form.start_time}
                        onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        data-testid="day-off-start-time"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                      <input
                        type="time"
                        value={form.end_time}
                        onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        data-testid="day-off-end-time"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="e.g., Vacation, Medical leave..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    data-testid="day-off-reason-input"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleAdd}
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    data-testid="save-day-off-btn"
                  >
                    {saving ? 'Saving...' : 'Add Day Off'}
                  </button>
                  <button
                    onClick={() => { setShowAddForm(false); setForm({ date: '', is_full_day: true, start_time: '09:00', end_time: '17:00', reason: '' }); }}
                    className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Affected Appointments Warning */}
          {showAffected && affectedAppointments.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="font-semibold text-amber-800 text-sm">
                    {affectedAppointments.length} appointment{affectedAppointments.length > 1 ? 's' : ''} affected
                  </p>
                  <p className="text-xs text-amber-600 mb-2">These users should be notified to reschedule or choose another staff member.</p>
                  <div className="space-y-1">
                    {affectedAppointments.map((apt, i) => (
                      <div key={i} className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                        {apt.patient_name} at {apt.time} {apt.service && `— ${apt.service}`}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowAffected(false)}
                className="text-xs text-amber-600 hover:text-amber-800 mt-1"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Days Off List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
            </div>
          ) : (
            <>
              {/* Upcoming */}
              {upcoming.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Upcoming</h4>
                  <div className="space-y-2">
                    {upcoming.map(d => (
                      <DayOffCard key={d.id} dayOff={d} onRemove={handleRemove} />
                    ))}
                  </div>
                </div>
              )}

              {/* Past */}
              {past.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Past</h4>
                  <div className="space-y-2 opacity-60">
                    {past.map(d => (
                      <DayOffCard key={d.id} dayOff={d} onRemove={handleRemove} isPast />
                    ))}
                  </div>
                </div>
              )}

              {upcoming.length === 0 && past.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">No days off scheduled</p>
                  <p className="text-xs mt-1">Click "Add Day Off" to schedule time off</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DayOffCard = ({ dayOff, onRemove, isPast = false }) => {
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2.5" data-testid={`day-off-card-${dayOff.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          dayOff.is_full_day ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
        }`}>
          <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {dayOff.is_full_day ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            )}
          </svg>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{formatDate(dayOff.date)}</p>
          <p className="text-xs text-gray-500">
            {dayOff.is_full_day ? 'Full day off' : `${dayOff.start_time} — ${dayOff.end_time}`}
            {dayOff.reason && ` · ${dayOff.reason}`}
          </p>
        </div>
      </div>
      {!isPast && (
        <button
          onClick={() => onRemove(dayOff.id)}
          className="text-gray-400 hover:text-red-500 p-1 transition-colors"
          data-testid={`remove-day-off-${dayOff.id}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default StaffDaysOffModal;
